import type { ParameterDefinition, ParameterValues } from '@axlabs/simulation-core';
import styles from './ParameterPanel.module.css';

interface ParameterPanelProps {
  parameters: readonly ParameterDefinition[];
  values: ParameterValues;
  onChange: (id: string, value: number) => void;
  disabled?: boolean;
}

export function ParameterPanel({ parameters, values, onChange, disabled }: ParameterPanelProps) {
  const isDisabled = disabled ?? false;

  return (
    <div className={styles.panel}>
      <h3 className={styles.title}>Parameters</h3>
      <div className={styles.parameters}>
        {parameters.map(param => {
          const paramDisabled = isDisabled || (param.derived ?? false);
          return (
            <ParameterControl
              key={param.id}
              parameter={param}
              value={values[param.id] ?? param.default}
              onChange={(value) => onChange(param.id, value)}
              disabled={paramDisabled}
            />
          );
        })}
      </div>
    </div>
  );
}

interface ParameterControlProps {
  parameter: ParameterDefinition;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

function ParameterControl({ parameter, value, onChange, disabled }: ParameterControlProps) {
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(parseFloat(e.target.value));
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = parseFloat(e.target.value);
    if (!isNaN(parsed)) {
      // Clamp to bounds
      const clamped = Math.max(parameter.min, Math.min(parameter.max, parsed));
      onChange(clamped);
    }
  };
  
  const percentage = ((value - parameter.min) / (parameter.max - parameter.min)) * 100;
  
  return (
    <div className={styles.control} data-disabled={disabled}>
      <div className={styles.header}>
        <label className={styles.name} title={parameter.description}>
          {parameter.name}
        </label>
        <span className={styles.symbol}>{parameter.symbol}</span>
      </div>
      
      <div className={styles.inputRow}>
        <input
          type="range"
          min={parameter.min}
          max={parameter.max}
          step={parameter.step}
          value={value}
          onChange={handleSliderChange}
          disabled={disabled}
          className={styles.slider}
          style={{ '--fill-percentage': `${percentage}%` } as React.CSSProperties}
        />
        <div className={styles.valueGroup}>
          <input
            type="number"
            min={parameter.min}
            max={parameter.max}
            step={parameter.step}
            value={value}
            onChange={handleInputChange}
            disabled={disabled}
            className={styles.input}
          />
          <span className={styles.unit}>{parameter.unit}</span>
        </div>
      </div>
      
      <div className={styles.bounds}>
        <span>{parameter.min}</span>
        <span>{parameter.max}</span>
      </div>
    </div>
  );
}
